import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { apiService } from '../services/api';
import { Banknote, Calendar, RefreshCw, Search, TrendingUp } from 'lucide-react';

const brl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return iso;
  }
};

interface SummaryBlock {
  totalBrl: number;
  count: number;
}

interface ListItem {
  id: string;
  kind?: string;
  username: string;
  lineId?: string | number;
  months?: number;
  totalBrl: number;
  mpTransactionAmount?: number | null;
  costCredits?: number;
  buyerMemberId?: number;
  credits?: number;
  packageLabel?: string;
  status: string;
  paymentProvider?: string;
  mpPaymentId?: string;
  updepixDepositId?: string;
  lynxChargeId?: string;
  paidAt?: string | null;
  completedAt?: string | null;
  failureReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export default function StorePayments() {
  const [access, setAccess] = useState<{
    canView: boolean;
    canManage: boolean;
    isOwner: boolean;
    cmsSlug?: string;
    reason?: string;
  } | null>(null);
  const canManage = access?.canManage === true;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [bulkStatus, setBulkStatus] = useState<string>('incomplete');
  const [bulkKind, setBulkKind] = useState<string>('all');
  const [bulkPreview, setBulkPreview] = useState<{ total: number; renewal: number; credit: number } | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [summary, setSummary] = useState<{
    today: SummaryBlock;
    month: SummaryBlock;
    last30Days: SummaryBlock;
    todayCompleted?: SummaryBlock;
    todayPaid?: SummaryBlock;
    monthCompleted?: SummaryBlock;
    monthPaid?: SummaryBlock;
    last30Completed?: SummaryBlock;
    last30Paid?: SummaryBlock;
    ranges?: { today?: { start: string; end: string }; month?: { start: string; end: string } };
  } | null>(null);

  const [byDay, setByDay] = useState<{ day: string; totalBrl: number; count: number }[]>([]);
  const [items, setItems] = useState<ListItem[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [renewalTotal, setRenewalTotal] = useState(0);
  const [creditTotal, setCreditTotal] = useState(0);
  const [page, setPage] = useState(1);
  const per = 15;
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [usernameSearch, setUsernameSearch] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');

  const loadAccess = useCallback(async () => {
    try {
      const res = await apiService.getStorePaymentsAccess();
      if (res?.success && res.data) {
        setAccess({
          canView: Boolean(res.data.canView),
          canManage: Boolean(res.data.canManage),
          isOwner: Boolean(res.data.isOwner),
          cmsSlug: res.data.cmsSlug,
          reason: res.data.reason
        });
        return Boolean(res.data.canView);
      }
      setAccess({ canView: false, canManage: false, isOwner: false, reason: res?.error });
      return false;
    } catch (e: any) {
      setAccess({
        canView: false,
        canManage: false,
        isOwner: false,
        reason: e?.response?.data?.error || e?.message
      });
      return false;
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const allowed = await loadAccess();
      if (!allowed) {
        setSummary(null);
        setByDay([]);
        setItems([]);
        setListTotal(0);
        setRenewalTotal(0);
        setCreditTotal(0);
        return;
      }

      const [sumRes, dayRes, listRes] = await Promise.all([
        apiService.getStorePaymentsSummary(),
        apiService.getStorePaymentsByDay(),
        apiService.getStorePaymentsList({
          page,
          per,
          status: statusFilter === 'all' ? undefined : statusFilter,
          username: usernameFilter.trim() || undefined
        })
      ]);

      if (sumRes?.success && sumRes.data) {
        setSummary({
          today: sumRes.data.today,
          month: sumRes.data.month,
          last30Days: sumRes.data.last30Days,
          todayCompleted: sumRes.data.todayCompleted,
          todayPaid: sumRes.data.todayPaid,
          monthCompleted: sumRes.data.monthCompleted,
          monthPaid: sumRes.data.monthPaid,
          last30Completed: sumRes.data.last30Completed,
          last30Paid: sumRes.data.last30Paid,
          ranges: sumRes.data.ranges
        });
      } else {
        setSummary(null);
      }

      if (dayRes?.success && dayRes.data?.points) {
        setByDay(dayRes.data.points);
      } else {
        setByDay([]);
      }

      if (listRes?.success && listRes.data) {
        setItems(listRes.data.items || []);
        setListTotal(listRes.data.total ?? 0);
        setRenewalTotal(Number(listRes.data.renewalTotal ?? 0));
        setCreditTotal(Number(listRes.data.creditTotal ?? 0));
      } else {
        setItems([]);
        setListTotal(0);
        setRenewalTotal(0);
        setCreditTotal(0);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Erro ao carregar pagamentos.';
      if (e?.response?.status === 403) {
        setAccess({
          canView: false,
          canManage: false,
          isOwner: false,
          reason: msg
        });
      } else {
        setErr(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, usernameFilter, loadAccess]);

  const handleBulkPreview = useCallback(async () => {
    if (!canManage) return;
    setBulkLoading(true);
    setBulkPreview(null);
    try {
      const res = await apiService.getStorePaymentsBulkPreview({
        status: bulkStatus === 'all' ? undefined : bulkStatus,
        kind: bulkKind as 'renewal' | 'credit' | 'all'
      });
      if (res?.success && res.data) {
        setBulkPreview({
          total: Number(res.data.total || 0),
          renewal: Number(res.data.renewal || 0),
          credit: Number(res.data.credit || 0)
        });
      } else {
        setErr(res?.error || 'Erro na pré-visualização.');
      }
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erro na pré-visualização.');
    } finally {
      setBulkLoading(false);
    }
  }, [bulkKind, bulkStatus, canManage]);

  const handleBulkClear = useCallback(async () => {
    if (!canManage || !bulkPreview?.total) return;
    const ok = window.confirm(
      `Remover ${bulkPreview.total} registro(s) de vendas deste painel?\n\nIsso não desfaz pagamentos na gateway — apenas apaga o histórico no CMS.`
    );
    if (!ok) return;
    setBulkLoading(true);
    try {
      const res = await apiService.clearStorePaymentRecords({
        status: bulkStatus === 'all' ? undefined : bulkStatus,
        kind: bulkKind as 'renewal' | 'credit' | 'all'
      });
      if (!res?.success) {
        setErr(res?.error || 'Nenhum registro removido.');
        return;
      }
      setBulkPreview(null);
      await loadAll();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erro ao limpar registros.');
    } finally {
      setBulkLoading(false);
    }
  }, [bulkKind, bulkPreview, bulkStatus, canManage, loadAll]);

  const removeItem = useCallback(
    async (item: ListItem) => {
      if (!canManage) return;
      const kind = (item.kind || '').toLowerCase() === 'credit' ? 'credit' : 'renewal';
      const ok = window.confirm(`Remover este registro (${kind})? Isso não desfaz pagamento/créditos, apenas remove o log.`);
      if (!ok) return;
      try {
        const res = await apiService.deleteStorePaymentRecord(item.id, kind);
        const deleted = Number(res?.data?.deleted ?? 0);
        if (!res?.success || deleted < 1) {
          setErr(
            (typeof res?.error === 'string' && res.error) ||
              'Nenhum registro foi removido. Verifique o tipo (renovação vs créditos) ou atualize a lista.'
          );
          await loadAll();
          return;
        }
        await loadAll();
      } catch (e: any) {
        setErr(e?.response?.data?.error || e?.message || 'Erro ao remover.');
      }
    },
    [canManage, loadAll]
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const totalPages = Math.max(1, Math.ceil(listTotal / per));

  if (access && !access.canView) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Pagamentos da loja</CardTitle>
            <CardDescription>
              {access.reason ||
                'O histórico de vendas registrado no aplicativo/loja é visível apenas para o dono deste painel (slug).'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Banknote className="w-8 h-8 text-emerald-600" />
            Pagamentos da loja
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Pagamentos aprovados (pagos) e renovações concluídas (Mercado Pago, Lynx e UpdePix).
            {access?.isOwner ? ' Você é o dono deste painel.' : ''}
          </p>
        </div>
        <Button variant="outline" onClick={() => loadAll()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Hoje (aprovados)</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              {brl(summary?.today?.totalBrl ?? 0)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {summary?.today?.count ?? 0} pagamento(s) aprovado(s) ·{' '}
              {summary?.todayPaid?.count ?? 0} pago(s) não renovado(s)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Mês atual (aprovados)</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              {brl(summary?.month?.totalBrl ?? 0)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {summary?.month?.count ?? 0} pagamento(s) aprovado(s) ·{' '}
              {summary?.monthPaid?.count ?? 0} pago(s) não renovado(s)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Últimos 30 dias</CardDescription>
            <CardTitle className="text-2xl">{brl(summary?.last30Days?.totalBrl ?? 0)}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {summary?.last30Days?.count ?? 0} pagamento(s) aprovado(s) ·{' '}
              {summary?.last30Paid?.count ?? 0} pago(s) não renovado(s)
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Por dia (últimos ~30 dias)</CardTitle>
          <CardDescription>Soma em BRL por data efetiva (pago/concluído) no fuso America/Sao_Paulo.</CardDescription>
        </CardHeader>
        <CardContent>
          {byDay.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum dado no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Dia</th>
                    <th className="py-2 pr-4">Total</th>
                    <th className="py-2">Pedidos</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay.map((row) => (
                    <tr key={row.day} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-4 font-mono">{row.day}</td>
                      <td className="py-2 pr-4">{brl(row.totalBrl)}</td>
                      <td className="py-2">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Limpeza em massa</CardTitle>
            <CardDescription>
              Remove registros do histórico deste slug (não estorna PIX/Mercado Pago). Use &quot;Não concluídos&quot;
              para apagar pendentes, falhas e pagos sem conclusão de renovação.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full sm:w-48">
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incomplete">Não concluídos (recomendado)</SelectItem>
                    <SelectItem value="failed">Somente falhou</SelectItem>
                    <SelectItem value="pending">Somente pendente</SelectItem>
                    <SelectItem value="paid">Somente pago (sem conclusão)</SelectItem>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="completed">Somente concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-48">
                <Select value={bulkKind} onValueChange={setBulkKind}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Renovação + créditos</SelectItem>
                    <SelectItem value="renewal">Só renovação</SelectItem>
                    <SelectItem value="credit">Só créditos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => void handleBulkPreview()} disabled={bulkLoading}>
                Pré-visualizar
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleBulkClear()}
                disabled={bulkLoading || !bulkPreview?.total}
              >
                Remover em massa
              </Button>
            </div>
            {bulkPreview && (
              <p className="text-sm text-muted-foreground">
                Serão removidos: <strong>{bulkPreview.total}</strong> ({bulkPreview.renewal} renovação,{' '}
                {bulkPreview.credit} créditos)
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Lista de pedidos</CardTitle>
            <CardDescription>
              {renewalTotal} renovação(ões) · {creditTotal} crédito(s) · {listTotal} no total
              {usernameFilter ? ` · filtro: "${usernameFilter}"` : ''}
            </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por usuário..."
                value={usernameSearch}
                onChange={(e) => setUsernameSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1);
                    setUsernameFilter(usernameSearch.trim());
                  }
                }}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setPage(1);
                setUsernameFilter(usernameSearch.trim());
              }}
            >
              Buscar
            </Button>
            {usernameFilter && (
              <Button
                variant="ghost"
                onClick={() => {
                  setUsernameSearch('');
                  setUsernameFilter('');
                  setPage(1);
                }}
              >
                Limpar
              </Button>
            )}
            <div className="w-full sm:w-44">
              <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="paid">Pago</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum pedido encontrado.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3">Provedor</th>
                      <th className="py-2 pr-3">Tipo</th>
                      <th className="py-2 pr-3">Usuário</th>
                      <th className="py-2 pr-3">Valor</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 hidden lg:table-cell">ID</th>
                      <th className="py-2 pr-3 hidden md:table-cell">Pago em</th>
                      <th className="py-2 hidden xl:table-cell">Concluído</th>
                      {canManage && <th className="py-2 pl-2 text-right">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((o) => {
                      const val = o.mpTransactionAmount != null ? o.mpTransactionAmount : o.totalBrl;
                      const providerKey = (o.paymentProvider || '').toLowerCase();
                      const prov =
                        providerKey === 'lynx'
                          ? 'Lynx'
                          : providerKey === 'updepix'
                            ? 'UpdePix'
                            : 'Mercado Pago';
                      const idRef =
                        providerKey === 'lynx'
                          ? (o.lynxChargeId || '—')
                          : providerKey === 'updepix'
                            ? (o.updepixDepositId || '—')
                            : (o.mpPaymentId || '—');
                      const kindLabel =
                        (o.kind || '').toLowerCase() === 'credit' ? 'Créditos' : 'Renovação';
                      return (
                        <tr key={o.id} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 pr-3 text-xs">{prov}</td>
                          <td className="py-2 pr-3 text-xs">{kindLabel}</td>
                          <td className="py-2 pr-3 font-medium">{o.username}</td>
                          <td className="py-2 pr-3">{brl(Number(val))}</td>
                          <td className="py-2 pr-3">
                            <span className="rounded px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800">
                              {o.status}
                            </span>
                          </td>
                          <td className="py-2 pr-3 hidden lg:table-cell font-mono text-xs truncate max-w-[120px]">
                            {idRef}
                          </td>
                          <td className="py-2 pr-3 hidden md:table-cell text-xs">{fmtDate(o.paidAt)}</td>
                          <td className="py-2 hidden xl:table-cell text-xs">{fmtDate(o.completedAt)}</td>
                          {canManage && (
                            <td className="py-2 pl-2 text-right">
                              <Button variant="outline" size="sm" onClick={() => void removeItem(o)} disabled={loading}>
                                Remover
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4 text-sm">
                <span className="text-muted-foreground">
                  {listTotal} registro(s) · página {page} de {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
